import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dichVuSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vatTuSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import {
  AccountModel,
  BoPhan,
  type ChiPhiKhongHopLeData,
  DichVu,
  DotThanhToan,
  type HoaDonBanHangDichVu,
  HopDong,
  KheUoc,
  Phi,
  type Tax,
  VatTu,
  VuViec
} from '@/types/schemas';
import {
  buildDataTableCols,
  type DataTableCol,
  type DataTableColumn,
  type DataTableRenderCellParams
} from '@/components/custom/erp/data-table-v2';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import { formatDate, formatMoney, formatRoundedNumber } from '@/lib/formatUtils';
import { buildInputTableCols } from '@/components/custom/erp/input-table-v2';

export const getDataTableColumns = (
  handleViewClick: (row: HoaDonBanHangDichVu) => void,
  totalAmountData?: number | null,
  isTotalLoading?: boolean
): DataTableColumn[] => {
  const total = (
    <div className='flex items-center gap-2'>
      <span className='text-lg font-bold'>&#931;</span>
      <span className='font-semibold text-main'>
        {isTotalLoading
          ? 'Đang tính...'
          : typeof totalAmountData === 'number'
            ? formatRoundedNumber(totalAmountData)
            : '—'}
      </span>
    </div>
  );

  const columns: DataTableCol<HoaDonBanHangDichVu>[] = [
    {
      field: 'status',
      headerName: 'Trạng thái',
      width: 120,
      valueGetter: row => row?.status,
      valueFormatter: value => {
        const status = String(value ?? '');
        switch (status) {
          case '0':
            return 'Chưa ghi sổ';
          case '3':
            return 'Chờ duyệt';
          case '5':
            return 'Đã ghi sổ';
          case '7':
            return 'Hủy';
          default:
            return 'Hủy';
        }
      },
      filter: {
        type: 'multiple',
        options: [
          { value: '0', label: 'Chưa ghi sổ' },
          { value: '3', label: 'Chờ duyệt' },
          { value: '5', label: 'Đã ghi sổ' },
          { value: '7', label: 'Hủy' }
        ]
      }
    },
    {
      field: 'ma_tthddt',
      headerName: 'Trạng thái HĐĐT',
      width: 150,
      valueGetter: row => (row?.ma_tthddt == '0' ? 'Không sử dụng' : 'Chờ phát hành')
    },
    {
      field: 'so_ct',
      headerName: 'Số c/từ',
      width: 150,
      renderCell: params => (
        <button
          onClick={e => {
            e.stopPropagation();
            handleViewClick(params.row);
          }}
          className='cursor-pointer hover:text-blue-500 hover:underline'
        >
          {params.row.so_ct}
        </button>
      )
    },
    {
      field: 'so_ct_hddt',
      headerName: 'Số hóa đơn',
      width: 120
    },
    {
      field: 'ngay_ct',
      headerName: 'Ngày c/từ',
      width: 120,
      valueGetter: row => row?.ngay_ct,
      valueFormatter: value => (value ? formatDate(value as BanHangRecord) : ''),
      filter: { type: 'date' }
    },
    {
      field: 'ma_kh',
      headerName: 'Mã khách hàng',
      width: 150,
      valueGetter: row => row?.ma_kh_data?.customer_code || row?.ma_kh || ''
    },
    {
      field: 'ten_kh',
      headerName: 'Tên khách hàng',
      width: 200,
      valueGetter: row => row?.ma_kh_data?.customer_name || ''
    },
    {
      field: 'dien_giai',
      headerName: 'Diễn giải',
      width: 250
    },
    {
      field: 'tk',
      headerName: 'Tk nợ',
      width: 120,
      valueGetter: row => row?.tk_data?.code || row?.tk || ''
    },
    {
      field: 't_tt_nt',
      headerName: 'Tổng tiền',
      width: 150,
      columnType: 'number',
      align: 'right',
      valueGetter: row => row?.t_tt_nt,
      valueFormatter: value => formatRoundedNumber(Number(value ?? 0)),
      filter: { type: 'text', after: total }
    },
    {
      field: 'ma_nt',
      headerName: 'Ngoại tệ',
      width: 120,
      valueGetter: row => row?.ma_nt_data?.ma_nt || row?.ma_nt || ''
    }
  ];

  return buildDataTableCols(columns);
};

export const getDetailTableColumns = (
  onCellValueChange?: (rowUuid: string, field: string, newValue: BanHangValue) => void,
  dienGiai?: string,
  cellError?: (rowUuid: string, field: string) => string | undefined
) => [
  {
    field: 'ma_dv',
    headerName: 'Mã dịch vụ',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<DichVu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DICH_VU}/`}
        searchColumns={dichVuSearchColumns}
        columnDisplay='ma_dv'
        dialogTitle='Danh mục dịch vụ'
        value={params.row.ma_dv_data?.ma_dv || ''}
        onRowSelection={(row: BanHangRecord) => {
          onCellValueChange?.(params.row.uuid, 'ma_dv_data', row);
        }}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_dv')}
        errorDisplay='tooltip'
        quickCreate={{
          key: QUERY_KEYS.DICH_VU,
          allowEdit: true
        }}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dv',
      resultField: 'ma_dv_data',
      detailEndpoint: QUERY_KEYS.DICH_VU
    }
  },
  {
    field: 'ten_dv',
    headerName: 'Tên dịch vụ',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField name='ten_dv' type='text' value={params.row.ma_dv_data?.ten_dv || ''} />
    ),
    nonWritable: true
  },
  {
    field: 'tk_dt',
    headerName: 'Tk doanh thu',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_dt_data?.code || params.row.ma_dv_data?.tk_dt_data?.code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'tk_dt_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'tk_dt')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'code',
      resultField: 'tk_dt_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'ten_tk',
    headerName: 'Tên tài khoản',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ten_tk'
        type='text'
        value={params.row.tk_dt_data?.name || params.row.ma_dv_data?.tk_dt_data?.name || ''}
      />
    ),
    nonWritable: true
  },
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 80,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='dvt'
        type='text'
        value={params.row.ma_dv_data?.dvt_data?.ten_dvt || params.row.dvt || ''}
        onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'dvt', newValue)}
      />
    )
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 180,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='so_luong'
        type='number'
        value={params.row.so_luong}
        placeholder='0'
        onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'so_luong', newValue)}
        error={cellError?.(params.row.uuid, 'so_luong')}
        errorDisplay='tooltip'
      />
    )
  },
  {
    field: 'gia_nt2',
    headerName: 'Giá VND',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='gia_nt2'
        type='number'
        placeholder='0'
        value={params.row.gia_nt2}
        onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'gia_nt2', newValue)}
      />
    )
  },
  {
    field: 'tien_nt2',
    headerName: 'Thành tiền VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='tien_nt2'
        type='number'
        value={params.row.tien_nt2 || 0}
        onValueChange={row => {
          onCellValueChange?.(params.row.uuid, 'tien_nt2', row);
        }}
      />
    )
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải/Tên hàng hóa dịch vụ',
    width: 250,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='dien_giai'
        type='text'
        value={params.row.dien_giai || dienGiai || ''}
        onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'dien_giai', newValue)}
      />
    )
  },
  {
    field: 'tl_ck',
    headerName: 'Tl ck(%)',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='tl_ck'
        type='number'
        placeholder='0'
        value={params.row.tl_ck == 0 ? '' : params.row.tl_ck}
        onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'tl_ck', newValue)}
      />
    )
  },
  {
    field: 'ck_nt',
    headerName: 'Ch.khấu VND',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ck_nt'
        type='number'
        value={params.row.ck_nt}
        onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'ck_nt', newValue)}
      />
    )
  },
  {
    field: 'tk_ck',
    headerName: 'Tk chiết khấu',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.ma_dv_data?.tk_ck_data?.code || params.row.tk_ck_data?.code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'tk_ck_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'code',
      resultField: 'tk_ck_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Tax>
        searchEndpoint={`/${QUERY_KEYS.THUE}/`}
        searchColumns={thueSearchColumns}
        dialogTitle='Danh mục thuế suất'
        value={params.row.ma_thue_data?.ten_thue}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_thue_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_thue',
      resultField: 'ma_thue_data',
      detailEndpoint: QUERY_KEYS.THUE
    }
  },
  {
    field: 'tk_thue_co',
    headerName: 'Tk thuế có',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_thue_co_data?.code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'tk_thue_co_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'code',
      resultField: 'tk_thue_co_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế VND',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='thue_nt'
        type='number'
        value={params.row.thue_nt}
        onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'thue_nt', newValue)}
      />
    )
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_bp_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_bp')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_vv_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_vv')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vu_viec',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<HopDong>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_hd_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_hd')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_dtt_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_dtt')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khế ước'
        value={params.row.ma_ku_data?.ma_ku || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_ku_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_ku')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_phi_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_phi')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_vt',
    headerName: 'Sản phẩm',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục sản phẩm'
        value={params.row.ma_sp_data?.ma_vt || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_sp_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_sp')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vt',
      resultField: 'ma_sp_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<BanHangRecord>
        disabled
        searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}/`}
        searchColumns={lenhSanXuatSearchColumns}
        columnDisplay='ma_lsx'
        dialogTitle='Danh mục lệnh sản xuất'
        value={params.row.ma_lsx_data?.ma_lsx || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_lsx_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_lsx')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lsx',
      resultField: 'ma_lsx_data',
      detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<ChiPhiKhongHopLeData>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
        searchColumns={chiPhiKhongHopLeSearchColumns}
        columnDisplay='ma_cp_khl'
        dialogTitle='Danh mục chi phí không hợp lệ'
        value={params.row.ma_cp0_data?.ma_cpkhl || params.row.ma_cp0_data?.ma_cp || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_cp0_data', row)}
        className='w-full'
        error={cellError?.(params.row.uuid, 'ma_cp0')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp_khl',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
    }
  }
];

export const getInputTableColumns = (): DataTableColumn[] =>
  buildInputTableCols<BanHangRecord>([
    {
      field: 'ma_dv',
      headerName: 'Mã dịch vụ',
      width: 120,
      renderCell: params => params.row.ma_dv_data?.ma_dv
    },
    {
      field: 'ten_dv',
      headerName: 'Tên dịch vụ',
      width: 200,
      renderCell: params => params.row.ma_dv_data?.ten_dv
    },
    {
      field: 'dvt',
      headerName: 'Đvt',
      width: 100,
      renderCell: params => params.row.dvt_data?.dvt
    },
    {
      field: 'so_luong',
      headerName: 'Số lượng',
      width: 100,
      renderCell: params => <CellField name='so_luong' type='number' value={params.row.so_luong} />
    },
    {
      field: 'gia_nt2',
      headerName: 'Giá VND',
      width: 120,
      renderCell: params => <CellField name='gia_nt2' type='number' value={params.row.gia_nt2} />
    },
    {
      field: 'tien_nt2',
      headerName: 'Thành tiền VND',
      width: 150,
      renderCell: params => <CellField name='tien_nt2' type='number' value={params.row.tien_nt2} />
    },
    {
      field: 'dien_giai',
      headerName: 'Diễn giải',
      width: 250
    },
    {
      field: 'tl_ck',
      headerName: 'Tl ck(%)',
      width: 100
    },
    {
      field: 'ck_nt',
      headerName: 'Ch.khấu VND',
      width: 120,
      renderCell: params => <CellField name='ck_nt' type='number' value={params.row.ck_nt} />
    },
    {
      field: 'thue_suat',
      headerName: 'Thuế suất',
      width: 100
    },
    {
      field: 'tk_thue_co',
      headerName: 'Tk thuế có',
      width: 120,
      renderCell: params => params.row.tk_thue_co_data?.code
    },
    {
      field: 'tk_dt',
      headerName: 'Tk doanh thu',
      width: 120,
      renderCell: params => params.row.tk_dt_data?.code
    },
    {
      field: 'tk_ck',
      headerName: 'Tk chiết khấu',
      width: 120,
      renderCell: params => params.row.tk_ck_data?.code
    },
    {
      field: 'thue_nt',
      headerName: 'Thuế VND',
      width: 120,
      renderCell: params => <CellField name='thue_nt' type='number' value={params.row.thue_nt} />
    },
    {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 120,
      renderCell: params => params.row.ma_bp_data?.ma_bp
    },
    {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 120,
      renderCell: params => params.row.ma_vv_data?.ma_vu_viec
    },
    {
      field: 'ma_hd',
      headerName: 'Hợp đồng',
      width: 120,
      renderCell: params => params.row.ma_hd_data?.ma_hd
    },
    {
      field: 'ma_dtt',
      headerName: 'Đợt thanh toán',
      width: 150,
      renderCell: params => params.row.ma_dtt_data?.ma_dtt
    },
    {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 120,
      renderCell: params => params.row.ma_ku_data?.ma_ku
    },
    {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 100,
      renderCell: params => params.row.ma_phi_data?.ma_phi
    },
    {
      field: 'ma_sp',
      headerName: 'Sản phẩm',
      width: 120,
      renderCell: params => params.row.ma_sp_data?.ma_vt
    },
    {
      field: 'lenh_sx',
      headerName: 'Lệnh sản xuất',
      width: 150
    },
    {
      field: 'ma_cp0',
      headerName: 'C/p không h/lệ',
      width: 150,
      renderCell: params => params.row.ma_cp0_data?.ma_cp
    }
  ]);
